class CollectionBrowseStateLayer {
  constructor () {
    this.eventBus = window.theme.collectionBrowse.eventBus

    this.state = {
      collectionHandle: this.collectionHandle,
      facets: [],
      pagination: {
        currentPage: this.currentPage,
        highestLoadedPage: this.currentPage,
        lowestLoadedPage: this.currentPage,
        loadedPages: [this.currentPage],
        lastIntent: 'replace',
        resultId: null,
        size: this.resultsPerPage,
        total: 0,
        totalPages: 0,
      },
      pageProducts: {},
      products: [],
      request: null,
      requestInFlight: false,
      requestMeta: null,
      requestSequence: 0,
      searchParams: new URLSearchParams(window.location.search),
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
    }

    this.subscribeToEvents()
  }

  subscribeToEvents () {
    this.eventBus.subscribe(
      'collectionBrowse:ui:params:changed',
      ({ intent = 'replace', searchParams }) => {
        const requestMeta = this.setSearchParams(searchParams, intent)

        if (!requestMeta) {
          return
        }

        this.eventBus.publish(
          'collectionBrowse:api:fetch:products',
          requestMeta,
        )
      },
    )

    this.eventBus.subscribe(
      'collectionBrowse:api:results:fetched',
      (response) => {
        const requestMeta =
          response.requestMeta || this.state.requestMeta || {}

        if (
          requestMeta.requestId &&
          requestMeta.requestId !== this.state.requestSequence
        ) {
          return
        }

        const currentPage =
          requestMeta.targetPage || response.request?.page || 1
        const pageProducts = (response.products.hits || []).map((product) => ({
          ...product,
          sourcePage: product.sourcePage || currentPage,
        }))
        const nextPageProducts = this.getNextPageProducts({
          intent: requestMeta.intent,
          pageProducts,
          targetPage: currentPage,
        })
        const loadedPages = this.getLoadedPages(nextPageProducts)
        const pagination = {
          currentPage,
          highestLoadedPage: loadedPages[loadedPages.length - 1] || currentPage,
          lastIntent: requestMeta.intent || 'replace',
          loadedPages,
          lowestLoadedPage: loadedPages[0] || currentPage,
          resultId: response.products.resultId || null,
          size:
            response.request?.num_results_per_page ||
            this.state.pagination.size ||
            this.resultsPerPage,
          total: response.products.total || 0,
          totalPages: Math.max(
            1,
            Math.ceil(
              (response.products.total || 0) /
                (response.request?.num_results_per_page ||
                  this.state.pagination.size ||
                  this.resultsPerPage),
            ),
          ),
        }

        this.state.pagination = pagination
        this.state.pageProducts = nextPageProducts
        this.state.products = pageProducts
        this.state.facets = response.products.facets || []
        this.state.request = response.request || null
        this.state.requestInFlight = false
        this.state.requestMeta = requestMeta
        this.state.sortBy = this.sortBy
        this.state.sortOrder = this.sortOrder

        this.eventBus.publish('collectionBrowse:state:facets:updated', {
          facets: this.state.facets,
          pagination: this.state.pagination,
          request: this.state.request,
        })

        this.eventBus.publish('collectionBrowse:state:products:updated', {
          pagination: this.state.pagination,
          products: this.state.products,
          request: this.state.request,
          selectedFilters: this.selectedFilters,
          sortBy: this.state.sortBy,
          sortOrder: this.state.sortOrder,
        })
      },
    )
  }

  setSearchParams (searchParams, intent = 'replace') {
    const nextSearchParams = new URLSearchParams(searchParams.toString())
    const filtersChanged = this.haveNonPageParamsChanged(nextSearchParams)
    const nextIntent = filtersChanged
      ? 'replace'
      : this.normalizeIntent(intent)

    if (filtersChanged) {
      nextSearchParams.delete('page')
    }

    this.state.searchParams = nextSearchParams
    const targetPage = this.getTargetPage(nextIntent)
    const requestId = this.state.requestSequence + 1
    const nextParamsString = nextSearchParams.toString()

    if (
      this.state.requestInFlight &&
      this.state.requestMeta?.intent === nextIntent &&
      this.state.requestMeta?.targetPage === targetPage &&
      this.state.requestMeta?.searchParams === nextParamsString
    ) {
      return null
    }

    this.state.requestInFlight = true
    this.state.requestMeta = {
      intent: nextIntent,
      requestId,
      targetPage,
      searchParams: nextParamsString,
    }
    this.state.requestSequence = requestId
    this.state.pagination.currentPage = targetPage
    this.state.sortBy = this.sortBy
    this.state.sortOrder = this.sortOrder

    return this.state.requestMeta
  }

  requestProducts () {
    const requestId = this.state.requestSequence + 1

    this.state.requestInFlight = true
    this.state.requestMeta = {
      intent: 'replace',
      requestId,
      targetPage: this.currentPage,
    }
    this.state.requestSequence = requestId

    this.eventBus.publish(
      'collectionBrowse:api:fetch:products',
      this.state.requestMeta,
    )
  }

  normalizeIntent (intent = 'replace') {
    if (intent === 'append-next' || intent === 'prepend-previous') {
      return intent
    }

    return 'replace'
  }

  haveNonPageParamsChanged (searchParams) {
    const previousParams = new URLSearchParams(
      this.state.searchParams.toString(),
    )
    const nextParams = new URLSearchParams(searchParams.toString())

    previousParams.delete('page')
    nextParams.delete('page')

    return previousParams.toString() !== nextParams.toString()
  }

  getTargetPage (intent = 'replace') {
    const highestLoadedPage = this.state.pagination.highestLoadedPage || 1
    const lowestLoadedPage = this.state.pagination.lowestLoadedPage || 1
    const requestedPage = this.currentPage
    const totalPages = this.state.pagination.totalPages || 1

    if (intent === 'append-next') {
      return Math.min(highestLoadedPage + 1, totalPages)
    }

    if (intent === 'prepend-previous') {
      return Math.max(lowestLoadedPage - 1, 1)
    }

    return requestedPage
  }

  getNextPageProducts ({ intent = 'replace', pageProducts = [], targetPage }) {
    if (intent === 'replace') {
      return {
        [targetPage]: pageProducts,
      }
    }

    return {
      ...this.state.pageProducts,
      [targetPage]: pageProducts,
    }
  }

  getLoadedPages (pageProducts = {}) {
    return Object.keys(pageProducts)
      .map((page) => Number(page))
      .filter((page) => Number.isFinite(page))
      .sort((firstPage, secondPage) => firstPage - secondPage)
  }

  get selectedFilters () {
    return this.state.facets.reduce((selectedFilters, facet) => {
      if (facet.type === 'range') {
        const rangeValue = this.getSelectedRangeFilterValue(facet)

        if (rangeValue) {
          selectedFilters[facet.field] = [rangeValue]
        }

        return selectedFilters
      }

      if (!Array.isArray(facet.data)) return selectedFilters

      const selectedValues = facet.data
        .filter((value) => value.selected)
        .map((value) => value.value)

      if (selectedValues.length === 0) return selectedFilters

      selectedFilters[facet.field] = selectedValues
      return selectedFilters
    }, {})
  }

  getSelectedRangeFilterValue (facet = {}) {
    if (facet.field !== 'price') return ''

    const min = this.state.searchParams.get('filter.v.price.gte')
    const max = this.state.searchParams.get('filter.v.price.lte')

    if (!min && !max) return ''

    return window.theme.constructorApiHelpers.buildRangeFacetValue({
      min,
      max,
    })
  }

  get currentPage () {
    return (
      Number(
        this.state?.searchParams?.get('page') || this.searchParams.get('page'),
      ) || 1
    )
  }

  get sortBy () {
    const sortValue =
      this.state?.searchParams?.get('sort_by') ||
      this.searchParams.get('sort_by') ||
      'best-selling'

    switch (sortValue) {
      case 'price-ascending':
        return 'price'
      case 'price-descending':
        return 'price'
      case 'title-ascending':
        return 'name'
      case 'title-descending':
        return 'name'
      case 'created-descending':
        return 'datePublished'
      default:
        return ''
    }
  }

  get sortOrder () {
    const sortValue =
      this.state?.searchParams?.get('sort_by') ||
      this.searchParams.get('sort_by') ||
      'best-selling'

    switch (sortValue) {
      case 'price-ascending':
      case 'title-ascending':
        return 'ascending'
      case 'price-descending':
      case 'title-descending':
      case 'created-descending':
        return 'descending'
      default:
        return ''
    }
  }

  get searchParams () {
    return new URLSearchParams(window.location.search)
  }

  get resultsPerPage () {
    const productGrid = document.querySelector('[data-cnstrc-browse]')
    const configuredResultsPerPage = Number(
      productGrid?.dataset?.cnstrcResultsPerPage,
    )

    return configuredResultsPerPage > 0
      ? configuredResultsPerPage
      : window.theme?.categoryConfig?.productsPerPage || 24
  }

  get collectionHandle () {
    const productGrid = document.querySelector('[data-cnstrc-browse]')

    return (
      productGrid?.dataset?.collectionHandle ||
      productGrid?.dataset?.cnstrcFilterValue ||
      ''
    )
  }
}

window.theme.collectionBrowse.stateLayer = new CollectionBrowseStateLayer()
